import { useContext } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import {
  Squares2X2Icon,
  Cog6ToothIcon,
  AdjustmentsHorizontalIcon,
  ShieldExclamationIcon,
  GlobeAltIcon,
  UsersIcon,
  ServerStackIcon,
  ServerIcon,
  MapPinIcon,
  QueueListIcon,
  PuzzlePieceIcon,
  CubeIcon,
  KeyIcon,
  LifebuoyIcon,
  ChartBarIcon,
  CodeBracketSquareIcon,
  NewspaperIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline'
import { useAuthStore } from '@/lib/auth'
import { SupportAdminSessionContext } from '@/contexts/SupportAdminSessionContext'

const primaryTabs = [
  { id: 'overview', label: 'Overview', path: '/admin', icon: Squares2X2Icon },
  { id: 'users', label: 'Users', path: '/admin/users', icon: UsersIcon },
  { id: 'servers', label: 'Servers', path: '/admin/servers', icon: ServerStackIcon },
  { id: 'nodes', label: 'Nodes', path: '/admin/nodes', icon: ServerIcon },
  { id: 'support', label: 'Support', path: '/admin/support', icon: LifebuoyIcon },
]

const supportStaffTabs = [
  { id: 'support', label: 'Tickets', path: '/admin/support', icon: LifebuoyIcon },
  { id: 'support-customers', label: 'Customers', path: '/admin/support/customers', icon: UsersIcon },
  { id: 'support-servers', label: 'Servers', path: '/admin/support/servers', icon: ServerStackIcon },
  { id: 'operational-logs', label: 'Operational Logs', path: '/admin/support/operational-logs', icon: QueueListIcon },
  { id: 'profile', label: 'Profile', path: '/account/profile', icon: UsersIcon },
  { id: 'security', label: 'Security', path: '/account/security', icon: KeyIcon },
]

const groupedTabs = [
  {
    label: 'Analytics',
    tabs: [
      { id: 'registration-funnel', label: 'Registration Funnel', path: '/admin/analytics/registration-funnel', icon: ChartBarIcon },
    ],
  },
  {
    label: 'Infrastructure',
    tabs: [
      { id: 'locations', label: 'Locations', path: '/admin/locations', icon: MapPinIcon },
      { id: 'nests', label: 'Nests', path: '/admin/nests', icon: CubeIcon },
      { id: 'dns', label: 'DNS', path: '/admin/dns', icon: GlobeAltIcon },
    ],
  },
  {
    label: 'Operations',
    tabs: [
      { id: 'operational-logs', label: 'Operational Logs', path: '/admin/support/operational-logs', icon: QueueListIcon },
      { id: 'moderation', label: 'Moderation', path: '/admin/moderation', icon: ShieldExclamationIcon },
      { id: 'tasks', label: 'Tasks', path: '/admin/tasks', icon: QueueListIcon },
      { id: 'integrations', label: 'Integrations', path: '/admin/integrations', icon: PuzzlePieceIcon },
      { id: 'updates', label: 'Updates', path: '/admin/updates', icon: NewspaperIcon },
    ],
  },
  {
    label: 'Platform',
    tabs: [
      { id: 'stripe', label: 'Stripe', path: '/admin/stripe', icon: Cog6ToothIcon },
      { id: 'package-billing', label: 'Packages', path: '/admin/package-billing', icon: CubeIcon },
      { id: 'api-keys', label: 'API Keys', path: '/admin/api-keys', icon: KeyIcon },
      { id: 'settings', label: 'Settings', path: '/admin/settings', icon: AdjustmentsHorizontalIcon },
    ],
  },
  {
    label: 'Developer Tools',
    tabs: [
      { id: 'configuration-registry', label: 'Configuration Registry', path: '/admin/developer/configuration-registry', icon: CodeBracketSquareIcon },
    ],
  },
]

const isTabActive = (pathname: string, path: string) => path === '/admin'
  ? pathname === '/admin'
  : pathname.startsWith(path)

export default function AdminSubnav() {
  const location = useLocation()
  const user = useAuthStore(state => state.user)
  const supportSession = useContext(SupportAdminSessionContext)

  if (!user?.root_admin) {
    return (
      <nav className="flex flex-wrap gap-2 rounded-t-xl border-b border-gray-200 bg-white px-4 py-3 dark:border-white/5 dark:bg-[#0a0a0c]" aria-label="Support navigation">
        {supportStaffTabs.filter(tab => tab.id !== 'operational-logs' || supportSession?.permissions.includes('support.logs.view')).map(tab => <NavItem key={tab.id} tab={tab} active={tab.path === '/admin/support' ? location.pathname === tab.path : isTabActive(location.pathname, tab.path)} />)}
      </nav>
    )
  }

  const overflowActive = groupedTabs.some(group => group.tabs.some(tab => isTabActive(location.pathname, tab.path)))

  return (
    <nav className="border-b border-gray-200 dark:border-white/5 bg-white dark:bg-[#0a0a0c] px-4 rounded-t-xl" aria-label="Admin navigation">
      <div className="flex items-center gap-2 py-3">
        {primaryTabs.map(tab => (
          <NavItem key={tab.id} tab={tab} active={isTabActive(location.pathname, tab.path)} />
        ))}

        <details className="relative group ml-auto">
          <summary className={`list-none cursor-pointer flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-full transition-colors [&::-webkit-details-marker]:hidden ${
            overflowActive
              ? 'text-white bg-gradient-to-r from-primary-600 to-primary-500'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-white/5'
          }`}>
            More
            <ChevronDownIcon className="w-4 h-4 transition-transform group-open:rotate-180" />
          </summary>
          <div className="absolute right-0 z-50 mt-2 w-72 rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-[#111318] shadow-xl p-2">
            {groupedTabs.map((group, index) => (
              <div key={group.label} className={index > 0 ? 'border-t border-gray-200 dark:border-white/5 mt-2 pt-2' : ''}>
                <p className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-gray-500">{group.label}</p>
                <div className="grid grid-cols-2 gap-1">
                  {group.tabs.map(tab => (
                    <NavLink
                      key={tab.id}
                      to={tab.path}
                      onClick={event => event.currentTarget.closest('details')?.removeAttribute('open')}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                        isTabActive(location.pathname, tab.path)
                          ? 'bg-primary-500/15 text-primary-400'
                          : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-white/5 hover:text-gray-900 dark:hover:text-white'
                      }`}
                    >
                      <tab.icon className="w-4 h-4" />
                      {tab.label}
                    </NavLink>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </details>
      </div>
    </nav>
  )
}

function NavItem({ tab, active }: {
  tab: typeof primaryTabs[number] | typeof supportStaffTabs[number]
  active: boolean
}) {
  return (
    <NavLink
      to={tab.path}
      aria-label={tab.label}
      className={`relative flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap ${
        active
          ? 'text-white bg-gradient-to-r from-primary-600 to-primary-500 shadow-[0_0_20px_rgba(59,130,246,0.3)]'
          : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-white/5'
      }`}
    >
      <tab.icon className="w-4 h-4" />
      <span className="hidden lg:inline">{tab.label}</span>
    </NavLink>
  )
}
